import React, { useCallback, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2, ExternalLink, ShieldCheck } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';

import { usePremium } from '../../hooks/usePremium';
import { useAuth } from '../../hooks/useAuth';
import { openStripePortal } from '../../lib/billing';
import { isNativeApp } from '../../utils/platform';
import { presentCustomerCenter } from '../../services/revenuecat';

export const SubscriptionManagementPage: React.FC = () => {
  const { user, loading } = useAuth();
  const { isPremium, planType, loading: premiumLoading } = usePremium();
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isNative = isNativeApp();

  const handleOpenPortal = useCallback(async () => {
    if (!user) {
      setError('You need to sign in to manage your subscription.');
      return;
    }

    setError(null);
    setPortalLoading(true);

    try {
      await openStripePortal(`${window.location.origin}/settings/subscription`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to open the billing portal.';
      const errorCode = (err as Error & { code?: string })?.code;

      // Provide helpful error messages based on error code
      let userFriendlyMessage = message;

      if (errorCode === 'NO_BILLING_HISTORY') {
        userFriendlyMessage = 'You don\'t have any billing history yet. Subscribe to premium to get started.';
      } else if (errorCode === 'NO_CUSTOMER_ID') {
        userFriendlyMessage = 'Unable to access your billing information. Please contact support if you have an active subscription.';
      } else if (errorCode === 'SUBSCRIPTION_NOT_FOUND') {
        userFriendlyMessage = 'No subscription found. Subscribe to premium to manage billing.';
      }

      setError(userFriendlyMessage);
      setPortalLoading(false);
    }
  }, [user]);

  const handleOpenCustomerCenter = useCallback(async () => {
    setError(null);
    setPortalLoading(true);

    try {
      await presentCustomerCenter();
    } catch (err) {
      console.error('Customer Center error:', err);
      setError('Unable to open subscription management. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  }, []);

  // Redirect to home if not authenticated (after all hooks)
  if (!loading && !user) {
    return <Navigate to="/popular" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-12 px-4">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-8">
        <div className="space-y-3">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary/80">
            Settings · Subscription
          </p>
          <h1 className="text-3xl font-bold text-foreground md:text-4xl">
            Manage Subscription
          </h1>
          <p className="text-base text-muted-foreground">
            {isNative
              ? 'Review your premium status and manage your subscription.'
              : 'Review your premium status and make billing changes through the Stripe Customer Portal.'
            }
          </p>
        </div>

        <Card className="bg-background/80">
          <CardHeader className="flex flex-row items-start justify-between">
            <div>
              <CardTitle className="text-xl">Subscription Status</CardTitle>
              <CardDescription>
                {premiumLoading ? 'Loading your subscription...' : isPremium ? (planType === 'special_3m' ? 'Special Premium (3m)' : 'Premium plan active') : 'Free plan'}
              </CardDescription>
            </div>
            <div className="rounded-full bg-primary/10 p-3">
              <ShieldCheck className="h-6 w-6 text-primary" />
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="rounded-md bg-muted/60 p-4 text-sm text-muted-foreground">
              <p>
                {isNative
                  ? 'Manage your subscription, request refunds, or change plans using the button below. Changes sync to Wallstreetle automatically.'
                  : 'Billing, cancellations, and payment updates all happen securely in Stripe. Any changes you make there sync back to Wallstreetle automatically within a few seconds.'
                }
              </p>
            </div>

            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                {error}
              </div>
            )}
          </CardContent>

          {planType !== 'special_3m' && (
            <CardFooter className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              {isNative ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    Cancel, change plans, or restore purchases.
                  </p>
                  <Button
                    className="w-full sm:w-auto"
                    onClick={handleOpenCustomerCenter}
                    disabled={portalLoading || premiumLoading}
                  >
                    {portalLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Opening...
                      </>
                    ) : (
                      'Manage Subscription'
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    You&apos;ll be redirected to Stripe in a new tab.
                  </p>
                  <Button
                    className="w-full sm:w-auto"
                    onClick={handleOpenPortal}
                    disabled={portalLoading || premiumLoading}
                  >
                    {portalLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Opening portal...
                      </>
                    ) : (
                      <>
                        Manage in Stripe
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </>
              )}
            </CardFooter>
          )}
        </Card>
      </div>
    </div>
  );
};

export default SubscriptionManagementPage;
